import { useRef, useState } from 'react';
import { getArea } from 'ol/sphere';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { resetAnnotationRefs } from '@utils/map/annotation/annotation.interaction';
import {
	setupFreehandDraw,
	removeFreehandDraw,
	checkAreaOfInterest,
} from '@utils/map/freeHand.draw';
import { setLayersVisibilityByCustomIds } from '@utils/map/helpers';
import { clearTooltip } from '@utils/map/tooltip.overlay';
import { formatArea } from '@utils/measurementSystem';

// Layers that would get in the way of annotating; shown again when the session ends.
const LAYERS_TO_HIDE = [
	'aoiLayer',
	'dsmLayer',
	'hexagonLayer',
	'trainingDataLayer',
	'centerLayer',
	'outlineLayer',
	'detailsLayer',
];

/**
 * Formats the area of a polygon geometry as a human-readable string, in the
 * measurement system set in the application (e.g. "10.50 m²").
 */
const getAreaString = geometry => {
	const [area, unit] = formatArea(getArea(geometry));
	return `${area} ${unit}`;
};

const setupDraw = ({ mapObject, tooltip, tooltipRef }) => {
	const { draw, layer, source } = setupFreehandDraw({
		type: 'Polygon',
		mapRef: mapObject,
		freehand: false,
	});

	// Show the area of the sketch beside the cursor while it is drawn
	draw.on('drawstart', function (e) {
		const sketch = e.feature;

		sketch.getGeometry().on('change', function (evt) {
			const geom = evt.target;
			tooltipRef.textContent = getAreaString(geom);
			tooltip.setPosition(geom.getInteriorPoint().getCoordinates());
		});
	});

	return { draw, layer, source };
};

// Resolves with the grid tiles inside the drawn polygon, or false after a toast
// when the polygon is unusable (too small, no tiles), so the caller can ask again.
const drawAOI = ({ grid, draw, source, mapTooltip, mode, addToast }) =>
	new Promise(resolve => {
		checkAreaOfInterest({ mode, grid, draw })
			.then(resolve)
			.catch(error => {
				console.warn(error);
				addToast({ id: 'draw-aoi-error', bg: 'danger', title: error });

				source.clear();
				clearTooltip({ ...mapTooltip });

				resolve(false);
			});
	});

/**
 * The boundary-draw step that precedes annotation mode: fetches the grid, hides
 * the layers in the way, asks for a polygon on the map and keeps the tiles
 * inside it. Shared by the Model view and Collection view entry buttons, which
 * differ only in how they fetch the grid and what they start with the tiles.
 *
 * `start` is guarded against a second click while the first is still fetching
 * or drawing: a second draw would overwrite the first's interaction and strand
 * it on the map with neither ESC nor Cancel able to reach it.
 *
 * `start` resolves true once the draw is live, false when the grid could not be
 * fetched (a toast says so), and undefined when a start is already under way.
 *
 * @returns {{
 *   start: (options: { fetchGrid: () => Promise<Array>, onReady: (tiles: Array, context: { boundary: Array }) => void }) => Promise<boolean|undefined>,
 *   cancel: () => void,
 *   isDrawing: boolean,
 *   layersToShow: string[],
 * }} `cancel` backs both the toolbar's Cancel button and ESC; `layersToShow`
 *   is what the annotators restore when the session ends.
 */
export const useAnnotationBoundaryDraw = () => {
	const { addToast } = useToast();
	const { projectMode, mapObject, mapTooltip, dispatch } = useProject();
	const { tooltip, tooltipRef } = mapTooltip || {};

	// The layers visible before annotation, to show again afterwards.
	const [layersToShow, setLayersToShow] = useState([]);

	// Held in a ref because both cancel affordances (the toolbar button and ESC)
	// run outside the closure that sets the draw interaction up.
	const drawRef = useRef(null);
	const [isDrawing, setIsDrawing] = useState(false);

	// Guards the window between the click and the draw step being set up. A ref
	// rather than state because the awaits resolve before React has re-rendered.
	const preparingRef = useRef(false);

	const getLayersToShow = () => {
		const layers = mapObject.getLayers()?.getArray();
		if (!layers?.length) return [];

		return layers.reduce((acc, layer) => {
			const { customLayerId, visible } = layer?.getProperties() ?? {};
			if (customLayerId && visible) acc.push(customLayerId);
			return acc;
		}, []);
	};

	const cleanupDraw = ({ draw, layer }) => {
		dispatch({ type: 'setDialogue', payload: null });
		dispatch({ type: 'setInteractionsDisabled', payload: false });

		removeFreehandDraw({ mapRef: mapObject, draw, layer });
		clearTooltip({ ...mapTooltip });

		// Reset any annotation references that may have been set before
		resetAnnotationRefs();
	};

	const enterBoundaryDraw = async ({ fetchGrid, onReady }) => {
		const grid = await fetchGrid();

		if (!grid || grid.length === 0) {
			console.error('Could not fetch grid for annotation');
			addToast({
				id: 'training-grid-error',
				bg: 'danger',
				title: 'An error occurred and annotation could not be started. Please try again later.',
			});
			return false;
		}

		const existingLayersVisibility = getLayersToShow();
		setLayersToShow(existingLayersVisibility);
		setLayersVisibilityByCustomIds(mapObject, LAYERS_TO_HIDE, false);

		dispatch({ type: 'setToolBarVisible', payload: false });
		dispatch({ type: 'setInteractionsDisabled', payload: true });
		dispatch({
			type: 'setDialogue',
			payload: {
				header: 'Set a boundary',
				body: 'Please draw a boundary around the area in the map where you want to make annotations. Press ESC to cancel.',
				fullWidth: false,
				autoClose: true,
			},
		});

		const { draw, layer, source } = setupDraw({ mapObject, tooltip, tooltipRef });

		// The polygon itself, for a session that later cuts another object's grid to the same area.
		let boundary = null;
		draw.on('drawend', e => {
			boundary = e.feature.getGeometry().getCoordinates();
		});

		// Snapshotted here rather than read from layersToShow, which is not up to date yet.
		drawRef.current = { draw, layer, layersVisibility: existingLayersVisibility };
		setIsDrawing(true);

		// Ask for a polygon until one holds tiles
		const drawAreaUntilTrue = async () => {
			const tiles = await drawAOI({
				grid,
				draw,
				source,
				mapTooltip,
				mode: projectMode,
				addToast,
			});
			if (!tiles) {
				await drawAreaUntilTrue();
				return;
			}

			cleanupDraw({ draw, layer });
			// Leaving the draw step also drops the ESC handler and the Cancel button
			drawRef.current = null;
			setIsDrawing(false);

			onReady(tiles, { boundary });
		};
		drawAreaUntilTrue();
		return true;
	};

	const start = async options => {
		if (preparingRef.current || drawRef.current) return undefined;
		preparingRef.current = true;

		try {
			return await enterBoundaryDraw(options);
		} finally {
			// The flow returns once the draw step is live; drawRef guards from there.
			preparingRef.current = false;
		}
	};

	/** Leaves the boundary-draw step without entering annotation mode. */
	const cancel = () => {
		const { draw, layer, layersVisibility } = drawRef.current ?? {};
		drawRef.current = null;
		setIsDrawing(false);

		if (draw && layer) cleanupDraw({ draw, layer });

		dispatch({ type: 'setToolBarVisible', payload: true });

		if (layersVisibility) setLayersVisibilityByCustomIds(mapObject, layersVisibility, true);
	};

	/**
	 * Takes the map over for a session that already has its area (one restored
	 * after a reload): the same hidden layers and toolbar as after a draw, with no
	 * draw step.
	 */
	const resume = () => {
		setLayersToShow(getLayersToShow());
		setLayersVisibilityByCustomIds(mapObject, LAYERS_TO_HIDE, false);
		dispatch({ type: 'setToolBarVisible', payload: false });
	};

	useKeyboardShortcut('Escape', isDrawing, cancel);

	return { start, cancel, resume, isDrawing, layersToShow };
};
